import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Layout from "@/components/Layout";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { RegulensMark } from "@/components/RegulensMark";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      if (!u.is_verified) navigate("/verify-email");
      else if (u.is_admin) navigate("/admin");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" offset={80} />
      <section className="max-w-md mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <RegulensMark size={44} theme="dark" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)] mt-3">Sign in to Regulens</h1>
          <p className="text-sm text-[var(--rg-text)] mt-1">Compliance intelligence with verified citations.</p>
        </div>
        <div className="space-y-4 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6" data-testid="login-form">
          <GoogleSignInButton testId="login-google-btn" />
          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-[var(--rg-border)]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--rg-muted)] font-bold">or</span>
            <div className="flex-1 h-px bg-[var(--rg-border)]" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="login-email" />
            </div>
            <div>
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold" data-testid="login-submit">
              {busy ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>
        <p className="text-center text-sm text-[var(--rg-text)] mt-4">
          New here? <Link to="/register" className="text-[var(--rg-sage)] hover:underline">Create an account</Link>
        </p>
      </section>
    </Layout>
  );
}
